import { Box } from '@mui/material';
import { api } from '@verifime/api-definition';
import { useCallback, useEffect, useRef } from 'react';
import { useFileUploaderContext } from './FileUploaderContext';
import { useFormContext } from './FormContext';
import { OcrEditor } from './OcrEditor';
import { useScrollTargetContext } from './ScrollTargetContext';
import { TOcrData } from './types';
import { buildOcrData } from './utils';
import { useWizardContext } from './WizardContext';
import isEqual from 'lodash/isEqual';

export default function FormView({ currentStep }: Readonly<{ currentStep: number }>) {
  const {
    state: { uploadIdDocumentConfig, countryCode, customerId },
    getSelectedDocumentTypeForStep,
    getUploadRequiredForSelectedDocumentTypeForStep,
  } = useWizardContext();

  const {
    state: { formData, validationResults, isValid },
    setFormData,
    setIsValid,
  } = useFormContext();

  const { registerRef } = useScrollTargetContext();

  const {
    state: { fileIds },
  } = useFileUploaderContext();

  const ocrEditorRef = useRef<HTMLDivElement>(null);
  const initializedFormDataRef = useRef({ prevSelectedDocumentType: '', initialized: false });

  // Register the ref with ScrollTargetContext
  useEffect(() => {
    registerRef('ocr-editor', ocrEditorRef);
  }, [registerRef]);

  const uploadRequired = getUploadRequiredForSelectedDocumentTypeForStep(currentStep);
  const selectedDocumentType = getSelectedDocumentTypeForStep(currentStep);

  useEffect(() => {
    if (
      initializedFormDataRef.current.initialized &&
      initializedFormDataRef.current.prevSelectedDocumentType === selectedDocumentType
    ) {
      return;
    }
    if (!customerId || !selectedDocumentType) {
      return;
    }

    if (uploadRequired) {
      if ((fileIds?.length || 0) < 1) {
        return;
      }
    }

    initializedFormDataRef.current = {
      prevSelectedDocumentType: selectedDocumentType,
      initialized: true,
    };
    api
      .getV2personIdidentityDocumentType({
        params: { id: customerId, type: selectedDocumentType },
      })
      .then((data) => {
        if (data) {
          setFormData(buildOcrData(selectedDocumentType, data));
        }
      });
  }, [customerId, selectedDocumentType, fileIds, uploadRequired]);

  const handleOcrEditorFormChange = useCallback(
    (newValid: boolean, newData: TOcrData) => {
      if (!isEqual(formData, newData)) {
        setFormData(newData);
      }
      if (isValid !== newValid) {
        setIsValid(newValid);
      }
    },
    [formData, isValid, setFormData, setIsValid],
  );

  if (selectedDocumentType === 'OtherIdentityDocument') {
    return null;
  }

  if (!formData) {
    return null;
  }

  return (
    <Box ref={ocrEditorRef} data-component="FormView">
      <OcrEditor
        countryCode={countryCode}
        initialData={formData}
        documentType={selectedDocumentType}
        onFormChange={handleOcrEditorFormChange}
        validationResults={validationResults}
      />
    </Box>
  );
}
